import {
  quitDayExpenseId,
  quitPlanId,
  type LocalDate,
  type QuitDayExpenseRef,
  type QuitPlanRef,
} from '@tahi/core';
import { funds, ledgerEntries, quitPlans } from '@tahi/db';
import { and, eq, isNull } from 'drizzle-orm';
import type { LocalDb } from './localDb';

export interface QuitReads {
  getQuitPlan(): QuitPlanRef | null;
  getQuitDayExpense(date: LocalDate): QuitDayExpenseRef | null;
}

/**
 * Prefetch for the synchronous ReadModel (conventions §15): the plan, and every day's Cigarettes
 * row (one per day at most, so this stays small).
 */
export async function loadQuitReads(db: LocalDb, userId: string): Promise<QuitReads> {
  const planRow = await db
    .select({ id: quitPlans.id, fundId: quitPlans.fundId, deletedAt: quitPlans.deletedAt })
    .from(quitPlans)
    .where(and(eq(quitPlans.userId, userId), eq(quitPlans.id, quitPlanId(userId))))
    .get();
  // A plan fund that was deleted reads as none, so the cost falls back to Budget's default.
  const fundLive = planRow?.fundId
    ? Boolean(
        await db
          .select({ id: funds.id })
          .from(funds)
          .where(
            and(eq(funds.userId, userId), eq(funds.id, planRow.fundId), isNull(funds.deletedAt)),
          )
          .get(),
      )
    : false;
  const plan: QuitPlanRef | null =
    planRow && !planRow.deletedAt
      ? { id: planRow.id, fundId: fundLive ? planRow.fundId : null }
      : null;
  const rows = await db
    .select({
      id: ledgerEntries.id,
      date: ledgerEntries.date,
      fundId: ledgerEntries.fundId,
      note: ledgerEntries.note,
      deletedAt: ledgerEntries.deletedAt,
    })
    .from(ledgerEntries)
    .where(and(eq(ledgerEntries.userId, userId), eq(ledgerEntries.sourceModule, 'quit_smoking')))
    .all();
  const byDate = new Map<LocalDate, QuitDayExpenseRef>();
  for (const r of rows) {
    if (r.id !== quitDayExpenseId(userId, r.date)) continue;
    byDate.set(r.date, {
      id: r.id,
      deleted: r.deletedAt !== null,
      fundId: r.fundId ?? null,
      note: r.note ?? null,
    });
  }
  return {
    getQuitPlan: () => plan,
    getQuitDayExpense: (date) => byDate.get(date) ?? null,
  };
}
